import type {
    BaseAssetDescription,
    CaptionsDescription,
    MediaAssetDescription,
    MusicDescription,
    SceneCoverage,
    SceneDescription,
    SceneGroupDescription,
    TemplateDescription,
    TextAssetDescription,
    TranscriptDescription,
    TranscriptWord,
    VideoDescription,
    VideoStateSnapshot,
} from '../types';

/**
 * GraphQL schema for querying video state.
 *
 * This is a *view* over the same {@link VideoStateSnapshot} the flat git-diff video state is
 * rendered from — not a second source of truth. The agent uses it to pull just the slice of state
 * it needs (a single scene, assets of one type, a group's transcript) instead of the whole dump.
 *
 * The schema deliberately mirrors the real shape of the data, which is shallow: Video → SceneGroup
 * → Scene → Asset, four levels, with the one unbounded list (a transcript's words) tucked behind
 * `SceneGroup.transcript`. There is nothing deep to traverse, which is the point — the schema is
 * what tells the model the state is flat.
 *
 * Scalar values are the snapshot's own pre-rendered strings (e.g. duration `"5000ms"`, a colour's
 * `"#0b1425 [slot 3]"`), so a queried value reads exactly as it would in the flat video state.
 */
export const VIDEO_STATE_SCHEMA_SDL = /* GraphQL */ `
    "An opaque JSON object — an asset's full property set, shaped like the flat video state."
    scalar JSON

    type Query {
        "The whole video. Start here."
        video: Video!

        "One scene by its short scene_id (exactly as shown in the video state). Null if no such scene."
        scene(id: ID!): Scene
    }

    type Video {
        resolution: String!
        aspectRatio: String!
        sceneCount: Int!
        duration: String!
        colorGrading: String
        designSystem: String
        template: Template!
        music: Music
        captions: Captions
        """
        The video's scene groups. Filter to narrow the result:
        - id: a short scene-group handle (prefix match, as shown in the video state)
        - type: one of "regular", "ai_voiceover", "recorded_voiceover", "ai_talking_head", "recorded_talking_head"
        - containsText: groups whose transcript contains this text (case-insensitive)
        """
        sceneGroups(id: ID, type: String, containsText: String): [SceneGroup!]!
    }

    type Template {
        template: String!
        fonts: String!
        colors: String!
        palettes: String!
    }

    type Music {
        id: String!
        volume: Float!
        title: String!
    }

    type Captions {
        enabled: Boolean!
        sceneDesignId: String
        bounds: String
        fontSize: Int
        textColor: String
        spokenWordHighlightColor: String
        font: String
    }

    type SceneGroup {
        "Short, stable scene-group handle (a git-style id prefix), as shown in the video state."
        id: ID!
        index: Int!
        type: String!
        duration: String!
        voice: String
        voiceSpeed: String
        voiceVolume: Float
        "Music level under this group (0-100), or \\"auto\\"."
        musicVolume: String
        fillerWords: Int
        """
        Null for text-on-media groups. The \`words\` list is the heavy field — pull it only when you
        need word-level timing. Use \`text\` or \`wordCount\` to read or size the narration cheaply.
        """
        transcript: Transcript
        "Scenes in this group. Filter with id (prefix match) or containsText (matches narration/text)."
        scenes(id: ID, containsText: String): [Scene!]!
    }

    type Transcript {
        language: String!
        wordCount: Int!
        "The spoken words joined into a string (pauses omitted). A cheap alternative to \`words\`."
        text: String!
        "Per-word timing. The large field — select it only when you need per-word times."
        words: [Word!]!
    }

    type Word {
        word: String!
        startTime: Int!
        endTime: Int!
        isPause: Boolean
    }

    type Scene {
        "Short, stable scene_id handle — the same handle every editing tool accepts."
        id: ID!
        globalIndex: Int!
        sceneGroupIndex: Int!
        design: String!
        sceneCategory: String
        duration: String!
        subscenes: Int!
        "The transcript words this scene plays over (voice-driven groups only)."
        covers: String
        "The covered words, each prefixed with the second it lands on (motion-graphics scenes only)."
        cues: String
        "This scene's role over its words: overlay | uncovered | speaker (voice-driven groups only)."
        role: String
        captionsVisible: Boolean
        "How this scene is entered from the one before, e.g. \\"Push (Up)\\" or \\"Match cut\\"; null for a straight cut."
        transitionIn: String
        textPositionPresets: [String!]!
        "Assets on this scene. Filter with type (e.g. \\"TEXT\\", \\"MEDIA\\", \\"ICON\\")."
        assets(type: String): [Asset!]!
    }

    type Asset {
        name: String!
        type: String!
        bounds: String!
        subscene: Int
        appearsAt: String
        text: String
        mediaId: String
        mediaType: String
        description: String
        """
        The asset's full property set as a JSON object — everything the flat video state would show
        for it. Opt into this only for an asset you are inspecting closely; selecting just type and
        name keeps the result small.
        """
        properties: JSON
    }
`;

/** A schema field, qualified by its type: `Scene.covers`. */
type SchemaField = `${string}.${string}`;

/**
 * The schema fields each field of a snapshot type backs, or `null` when it is deliberately not
 * exposed. Required over every key, so a field added to a snapshot type doesn't compile until it is
 * mapped here. A spec checks the mapped fields against the SDL above and the resolvers.
 */
type FieldSources<T> = { readonly [K in keyof T]-?: readonly SchemaField[] | null };

export const SCHEMA_FIELD_SOURCES = {
    snapshot: {
        timestamp: null,
        video: ['Query.video'],
        template: ['Video.template'],
        music: ['Video.music'],
        captions: ['Video.captions'],
        scenes: ['Query.scene', 'SceneGroup.scenes'],
        sceneGroups: ['Video.sceneGroups'],
    } satisfies FieldSources<VideoStateSnapshot>,
    video: {
        resolution: ['Video.resolution'],
        aspectRatio: ['Video.aspectRatio'],
        sceneCount: ['Video.sceneCount'],
        duration: ['Video.duration'],
        colorGrading: ['Video.colorGrading'],
        designSystem: ['Video.designSystem'],
    } satisfies FieldSources<VideoDescription>,
    template: {
        template: ['Template.template'],
        templateFonts: ['Template.fonts'],
        templateColors: ['Template.colors'],
        templatePalettes: ['Template.palettes'],
    } satisfies FieldSources<TemplateDescription>,
    music: {
        id: ['Music.id'],
        volume: ['Music.volume'],
        title: ['Music.title'],
    } satisfies FieldSources<MusicDescription>,
    captions: {
        enabled: ['Captions.enabled'],
        sceneDesignId: ['Captions.sceneDesignId'],
        bounds: ['Captions.bounds'],
        fontSize: ['Captions.fontSize'],
        textColor: ['Captions.textColor'],
        spokenWordHighlightColor: ['Captions.spokenWordHighlightColor'],
        font: ['Captions.font'],
    } satisfies FieldSources<CaptionsDescription>,
    sceneGroup: {
        id: ['SceneGroup.id'],
        index: ['SceneGroup.index'],
        type: ['SceneGroup.type'],
        sceneIndices: ['SceneGroup.scenes'],
        duration: ['SceneGroup.duration'],
        transcript: ['SceneGroup.transcript'],
        voice: ['SceneGroup.voice'],
        voiceSpeed: ['SceneGroup.voiceSpeed'],
        voiceVolume: ['SceneGroup.voiceVolume'],
        musicVolume: ['SceneGroup.musicVolume'],
        fillerWords: ['SceneGroup.fillerWords'],
    } satisfies FieldSources<SceneGroupDescription>,
    transcript: {
        language: ['Transcript.language'],
        words: ['Transcript.words', 'Transcript.wordCount', 'Transcript.text'],
    } satisfies FieldSources<TranscriptDescription>,
    word: {
        word: ['Word.word'],
        startTime: ['Word.startTime'],
        endTime: ['Word.endTime'],
        isPause: ['Word.isPause'],
    } satisfies FieldSources<TranscriptWord>,
    scene: {
        id: ['Scene.id'],
        globalIndex: ['Scene.globalIndex'],
        sceneGroupIndex: ['Scene.sceneGroupIndex'],
        design: ['Scene.design'],
        sceneCategory: ['Scene.sceneCategory'],
        duration: ['Scene.duration'],
        subscenes: ['Scene.subscenes'],
        coverage: ['Scene.covers', 'Scene.cues', 'Scene.role'],
        captionsVisible: ['Scene.captionsVisible'],
        transitionIn: ['Scene.transitionIn'],
        textPositionPresets: ['Scene.textPositionPresets'],
        assets: ['Scene.assets'],
    } satisfies FieldSources<SceneDescription>,
    coverage: {
        words: ['Scene.covers'],
        cues: ['Scene.cues'],
        role: ['Scene.role'],
    } satisfies FieldSources<SceneCoverage>,
    asset: {
        name: ['Asset.name'],
        type: ['Asset.type'],
        bounds: ['Asset.bounds'],
        subscene: ['Asset.subscene'],
        appearsAt: ['Asset.appearsAt'],
    } satisfies FieldSources<BaseAssetDescription>,
    // Each asset type's own fields reach the schema through Asset.properties; these few are also
    // promoted to fields of their own, so they can be selected without the whole property set.
    promotedAsset: {
        text: ['Asset.text'],
        mediaId: ['Asset.mediaId'],
        mediaType: ['Asset.mediaType'],
        description: ['Asset.description'],
    } satisfies Pick<FieldSources<TextAssetDescription>, 'text'> &
        Pick<FieldSources<MediaAssetDescription>, 'mediaId' | 'mediaType' | 'description'>,
};

/** Schema fields resolved from a whole snapshot object rather than from one of its fields. */
export const WHOLE_OBJECT_FIELDS: readonly SchemaField[] = ['Asset.properties'];
